import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Chip,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import useGetComments from '@core/comments/hooks/useGetComments';
import { getComments, getComment } from '@core/selectors/comments';
import { currentUser } from '@core/selectors/user';
import { isInstructor } from '@core/selectors/classroom';
import { AulaState } from '@core/types/state';
import formatDate from '@core/utils/formatDate';
import { UB_COMMENT_ITEM_TYPE } from '@app/utils/itemTypeForAnalytics';
import {
  ReactionList,
  ReactionPicker,
} from '@components/presentational/ResponsiveReactions';
import ResponsiveEditorReadOnly from '../Shared/ResponsiveEditorReadOnly';
import { Comment } from '../../../Comments/types';

const INITIAL_VISIBLE_COMMENTS = 5;

type ResponsivePostCommentsProps = {
  postId: string;
  readOnly?: boolean;
};

const getName = (user?: Comment['user']) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown user';

const CommentContent = ({ comment }: { comment: Comment }) => {
  if (!comment.content) return null;

  try {
    const content = JSON.parse(comment.content);
    return (
      <ResponsiveEditorReadOnly
        key={comment.editedAt || comment.id}
        itemId={comment.id}
        editorState={content}
        itemType="CommonRoom"
      />
    );
  } catch {
    return <Typography component="p">{comment.content}</Typography>;
  }
};

const CommentItem = ({
  id,
  isReply = false,
  readOnly = false,
}: {
  id: string;
  isReply?: boolean;
  readOnly?: boolean;
}) => {
  const theme = useTheme();
  const comment = useSelector((state: AulaState) => getComment(state, id)) as
    | Comment
    | undefined;
  const authorIsEducator = useSelector((state: AulaState) =>
    isInstructor(state, comment?.user?.objectId)
  );

  if (!comment?.user) return null;

  const name = getName(comment.user);
  const initials =
    (comment.user.firstName?.charAt(0) || 'U').toUpperCase() +
    (comment.user.lastName?.charAt(0) || '').toUpperCase();

  return (
    <Box
      component="article"
      aria-label={`Comment by ${name}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...(isReply && {
          ml: { xs: 2, md: 6 },
          mr: { xs: 2, md: 6 },
        }),
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}
      >
        <Avatar
          altText={name}
          fallbackText={initials}
          kind="circular"
          src={comment.user.avatar}
          sx={{ width: 40, height: 40, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 0.75,
              rowGap: 0.5,
            }}
          >
            <Typography
              sx={{
                color: theme.palette.text.primary,
                fontSize: '1rem',
                fontWeight: theme.typography.fontWeightMedium,
                lineHeight: 1.5,
                overflowWrap: 'anywhere',
              }}
            >
              {name}
            </Typography>
            {authorIsEducator && (
              <Chip
                label="Educator"
                size="small"
                sx={{
                  height: 24,
                  color:
                    theme.palette.mode === 'dark'
                      ? '#005B6C'
                      : theme.palette.primary.contrastText,
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#B4E8FB' : '#027D99',
                  '& .MuiChip-label': { px: 0.75, fontSize: '0.8125rem' },
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.8125rem',
              lineHeight: 1.5,
            }}
          >
            {formatDate(comment.createdAt)}
            {comment.editedAt ? ' (Edited)' : ''}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          pl: 6,
          color: theme.palette.text.primary,
          fontSize: '0.9375rem',
          lineHeight: 1.6,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          '& p': { m: 0 },
          '& *': { maxWidth: '100%' },
        }}
      >
        <CommentContent comment={comment} />
      </Box>
      <ReactionList
        hideWhenNoReactions
        itemId={comment.id}
        itemType={UB_COMMENT_ITEM_TYPE}
        readOnly={readOnly}
        sx={{ pl: '48px' }}
      />
      {!readOnly && (
        <Box
          aria-label="Comment actions"
          sx={{
            pl: '38px',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minHeight: 32,
            color: theme.palette.text.secondary,
          }}
        >
          <ReactionPicker itemId={comment.id} itemType={UB_COMMENT_ITEM_TYPE} />
          <Box component="span" sx={{ width: 12 }} />
          <Icons.Reply aria-hidden="true" sx={{ fontSize: 20 }} />
          <Typography variant="caption">Reply</Typography>
        </Box>
      )}
      {!isReply && comment.replies?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {comment.replies.map((replyId) => (
            <CommentItem
              key={replyId}
              id={replyId}
              isReply
              readOnly={readOnly}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const ResponsivePostComments = ({
  postId,
  readOnly = false,
}: ResponsivePostCommentsProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COMMENTS);
  const query = useGetComments(CommentsEntityTypes.post, postId);
  const comments = useSelector((state: AulaState) =>
    getComments(state, postId, CommentsEntityTypes.post)
  ) as string[];
  const user = useSelector(currentUser);
  const commentIds = query.isSuccess
    ? comments
    : query.data?.commentsIds || comments;
  const visibleIds = expanded
    ? commentIds.slice(Math.max(0, commentIds.length - visibleCount))
    : [];
  const hiddenCount = commentIds.length - visibleCount;
  const userName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'You';

  const toggleComments = () => setExpanded((value) => !value);

  return (
    <Box
      data-testid="responsive-post-comments"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {commentIds.length > 0 && (
        <Box
          component="button"
          onClick={toggleComments}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? 'Hide comments'
              : `Show ${commentIds.length} comment${
                  commentIds.length === 1 ? '' : 's'
                }`
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            alignSelf: 'flex-start',
            p: 0,
            border: 0,
            background: 'transparent',
            color: theme.palette.text.primary,
            cursor: 'pointer',
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.9375rem',
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {expanded
            ? 'Hide comments'
            : `Show ${commentIds.length} comment${
                commentIds.length === 1 ? '' : 's'
              }`}
          <Icons.KeyboardArrowDown
            sx={{
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
      )}

      {expanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {hiddenCount > 0 && (
            <Box
              component="button"
              onClick={() => setVisibleCount(commentIds.length)}
              sx={{
                alignSelf: 'flex-start',
                p: 0,
                border: 0,
                background: 'transparent',
                color: theme.palette.primary.main,
                cursor: 'pointer',
                textAlign: 'center',
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.875rem',
                fontWeight: theme.typography.fontWeightMedium,
              }}
            >
              See {hiddenCount} previous comment{hiddenCount === 1 ? '' : 's'}
            </Box>
          )}
          {visibleIds.map((id, index) => (
            <Box
              key={id}
              sx={{
                pb: index === visibleIds.length - 1 ? 0 : 2,
                borderBottom:
                  index === visibleIds.length - 1
                    ? 0
                    : `1px solid ${theme.palette.divider}`,
              }}
            >
              <CommentItem id={id} readOnly={readOnly} />
            </Box>
          ))}
        </Box>
      )}

      <Box
        aria-label="Add a comment"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
      >
        <Avatar
          altText={userName}
          fallbackText={(user?.firstName?.charAt(0) || 'Y').toUpperCase()}
          kind="circular"
          src={user?.avatar}
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            borderRadius: 1,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
            fontSize: '0.875rem',
          }}
        >
          Add a comment
        </Box>
      </Box>
    </Box>
  );
};

export default ResponsivePostComments;
